import { FieldArray, Form, FormikProvider, useFormik } from "formik";
import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Button, Card, CardBody, Col, FormFeedback, Input, Label, Row, Table, Container, } from "reactstrap";
import * as Yup from "yup";
import "./style.scss";
import { isEmpty } from "lodash";
import NoDataComponent from '../EntityMoreInformation/NoDataComponent';
import { dateFormat, decryptedValue, isPermission } from "common/utility";
import { callGetFinancialsSummary } from "http/fundLync/entityDocuments.helper";
import PropTypes from "prop-types";
import { addCamData, editCamData, getCamData } from "http/fundLync/cam.helper";
import API_SERVICES from "constants/app.constants";
import ToastDialog from "components/Common/ToastDialog";

const EntityDataTableCam = ({ data, columns }) => {
  const formatAmount = (amount) => {
    if (typeof amount === 'number' || (typeof amount === 'string' && !isNaN(amount))) {
      const numericAmount = Number(amount);
      const amountInCrores = numericAmount / 100; // Convert lakhs to crores
      const formattedAmount = amountInCrores === 0
        ? '₹ 0'
        : `₹ ${amountInCrores.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      return formattedAmount;
    }
    return amount;
  };
  const rowsToSkipFormatting = [
    'TOL/ TNW',
    'EBITDA / Interest (times)',
    'Current Ratio',
    'Debt Equity Ratio',
    'Debtors to sales (days)',
    'Creditors to purchases (days)',
    'Inventory stock (Days)',
    'Cash Conversion (in days)',
    'EBITDA %',
    'PAT %'
  ];

  const formatToTwoDecimal = (num) => {
    if (typeof num === 'number') {
      return num.toFixed(2);
    } else if (typeof num === 'string' && !isNaN(num)) {
      return parseFloat(num).toFixed(2);
    }
    return num;  // Return the original value if it's not a valid number
  };

  return (
    <React.Fragment>
      {!isEmpty(data) ? <div className="table-responsive react-table">

        <Table hover bordered>
          <thead className='dark'>
            <tr>
              {!isEmpty(columns) && columns.map((column, index) => {
                const isYear = column.key !== 'title';
                return <th className={isYear ? 'right-align' : ''} key={`index-${column.name}`}>{column.name}</th>
              })}
            </tr>
          </thead>
          <tbody>
            {!isEmpty(data) && data.map((entity, index) => {
              return (
                <>
                  <tr key={`index-${entity.key}`} className='font-weight-bold bg-light'><td colSpan={columns.length} className='font-weight-bold bg-none' color="light"><span className='font-weight-bold'>{entity.key}</span></td></tr>

                  {!isEmpty(entity.data) && entity.data.map((row, index) => {
                    return (
                      <tr key={`index-${entity.key}`}>
                        {!isEmpty(columns) && columns.map((column) => {
                          const isAmountColumn = column.key !== 'title';
                          const shouldSkipFormatting = rowsToSkipFormatting.includes(row.title);

                          return <td key={`index2-${row[column.key]}`} className={isAmountColumn ? 'right-align' : ''}>
                            {row[column.key] && row[column.key] !== null
                              ? (shouldSkipFormatting || entity.key == "Ratios"
                                ? formatToTwoDecimal(row[column.key])
                                : formatAmount(row[column.key]))
                              : API_SERVICES.EMPTY_VALUE}
                          </td>;
                        })}
                      </tr>
                    )
                  })}
                </>
              )
            })}
          </tbody>
        </Table>
      </div> :
        <NoDataComponent title='No Data' subtitle='No Data Available' />}
    </React.Fragment>
  )
}

const CamFinDetails = forwardRef((props, ref) => {
  document.title = "Financials Summary | FundLync";
  const { queryString } = props; 
  const [data1, setData] = useState([]);
  const [columns1, setColumns] = useState([]);
  const [camData, setCamData] = useState([])
  const urlParams = new URLSearchParams(window.location.search);
  const encodedDealId = urlParams.get("dealId");
  const dealId = atob(encodedDealId);
  const [entityId, setEntityId] = useState('')
  const [encodedId, setEncodedId] = useState('')
  const [toastInfo, setToastInfo] = useState({ showToast: false });
  
  useEffect(() => {
    const userDetails = decryptedValue(localStorage.getItem("user"));
    if (userDetails.entityType === "Partner" || userDetails.entityType === "Owner") {
      setEncodedId(urlParams.get("id"))
      setEntityId(atob(encodedId))
    } else {
      setEncodedId(entityId)
      setEntityId(userDetails.entityId)
    }
  }, [entityId, encodedId])
  const getEntityData = async () => {
    const bodyData = {
      entityId: entityId
    };

    const entityInfo = await callGetFinancialsSummary(bodyData);
    if (entityInfo.status) {
      transformData(entityInfo.data);
    }
  };
  let columns = []
  let data = []
  const transformData = (apiData) => {
    if (!isEmpty(apiData) && Array.isArray(apiData)) {
      let newColumns = []
      let tempCol = apiData.map((dataItem, index) => {
        let year = dataItem.filter((item) => item.key === 'year')
        let yearValue = year[0].value
        const isProvisional = dataItem.some(item => item.key === 'isProvisional' && item.value === true);
        if (isProvisional) {
          const year = parseInt(yearValue, 10);
                if (!isNaN(year)) {
                    if (year === new Date().getFullYear()) {
                      yearValue = `FY ${year - 1}-${year % 100} (Provisional)`; // Current year format, e.g., 2024 -> Provisional FY 2023-24
                    } else {
                      yearValue =  `FY ${year - 1}-${year % 100} (Provisional)`; // Previous years format, e.g., 2023 -> FY 2022-23
                    }
                }
        } else {
          const year = parseInt(yearValue, 10);
                if (!isNaN(year)) {
                    if (year === new Date().getFullYear()) {
                      yearValue = `FY ${year - 1}-${year % 100}`; // Current year format, e.g., 2024 -> Provisional FY 2023-24
                    } else {
                      yearValue = `FY ${year - 1}-${year % 100}`; // Previous years format, e.g., 2023 -> FY 2022-23
                    }
                }
        }
        return yearValue;
      });

      newColumns = [...newColumns, {
        name: 'Particulars (Amount Cr.)',
        selector: 'title',
        key: 'title',
        sortable: true,
      }]
      let tempColumns = tempCol.map((item, index) => {
        return {
          name: item,
          selector: item,
          key: item,
          sortable: true,
        }
      })
      columns = [...newColumns, ...tempColumns]
      setColumns(columns)
    }

    let tempData = {}
    const key = "Financial Summary"
    const transformedData = {};

    apiData.forEach((row, index) => {
      row.forEach(item => {
        if (item.key === "isProvisional") return;
        if (item.key === "year") return;
        if (!transformedData[item.displayName]) {
          transformedData[item.displayName] = {};
        }
        transformedData[item.displayName][columns[index + 1]['key']] = item.value;
      });
    });

    const finalData = Object.keys(transformedData).map(title => ({
      title,
      ...transformedData[title],
    }));

    tempData = {
      key: key,
      data: finalData
    }

    data = [...data, tempData]
    setData(data)
  }  
  useEffect(() => {
    const loadData = async () => {
      try {
        // Fetch the CAM data
        const response = await getCamData({ dealId });
        setCamData([response?.data[0]]);
        const data = response?.data[0]
        // if (response?.status && data?.finSummary != null) {
        //   setColumns(data?.finSummary?.columns);
        //   setData(data?.finSummary?.data);
        // } else {
          getEntityData();
        // }

      } catch (error) {
        console.error("Error loading data:", error);
      }
    };
    loadData()
  }, [entityId]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      financialData: {
        columns: columns1 || [],
        data: data1 || []
      }

    },
    validationSchema: Yup.object({
      // financialData: Yup.array().of(

      // ),
    }),
    onSubmit: (values) => {
      if (camData.length > 0) {
        const entity = {
          aboutSection: camData[0]?.aboutSection,
          directorSharehodlerList: camData[0]?.directorSharehodlerList,
          keyStrength: camData[0]?.keyStrength,
          finSummary: values.financialData,
          debtProfile: camData[0]?.debtProfile,
          customerSupplier: camData[0]?.customerSupplier,
          termsheet: camData[0]?.termsheet,
          entityId: entityId,
          dealId: dealId,
          id: camData[0]?.id,
          lastVisitedStep: "debt-profile" || null,
        }
        editCamData(entity)
          .then((response) => {
            if (response.code == 201 && response.status) {
              setToastInfo({
                message: "Cam Data Updated",
                showToast: true,
                role: "success",
              });
            } else {
              const { message } = response;
              setToastInfo({
                message: message,
                showToast: true,
                role: "error",
              });
            }
          })
          .catch((err) => {
            console.log(err);
          });
      } else {
        const entity = {
          aboutSection: camData[0]?.aboutSection,
          directorSharehodlerList: camData[0]?.directorSharehodlerList,
          keyStrength: camData[0]?.keyStrength,
          finSummary: values.financialData,
          debtProfile: camData[0]?.debtProfile,
          customerSupplier: camData[0]?.customerSupplier,
          termsheet: camData[0]?.termsheet,
          entityId: entityId,
          dealId: dealId,
          id: camData[0]?.id,
          lastVisitedStep: "debt-profile" || null,
        };
        addCamData(entity)
          .then((response) => {
            if (response.code == 201 && response.status) {
              setToastInfo({
                message: "Cam Data Saved",
                showToast: true,
                role: "success",
              });
            } else {
              const { message } = response;
              setToastInfo({
                message: message,
                showToast: true,
                role: "error",
              });
            }
          })
          .catch((err) => {
            console.log(err);
          });
      }
    },
  });

  useImperativeHandle(ref, () => ({
    handleSubmit: () => validation.handleSubmit(),
    getValidationStatus: () =>
      validation.validateForm().then((errors) => Object.keys(errors).length === 0),
  }));

  return (

    <React.Fragment>
      <div
        className="d-flex justify-content-between mt-1"
        style={{ cursor: "pointer" }}
      >
        <div>
          <h5 style={{ color: "#42B763" }}>Financials Summary</h5>
        </div>
      </div>

      <Row>
        <Col xs="12" className="mb-2">
          <Container fluid>

            <div>

              <Card style={{ border: "none" }}>
                <CardBody className="px-0">
                  <EntityDataTableCam data={data1} columns={columns1} />
                </CardBody>
              </Card>
            </div>
          </Container>
        </Col>
      </Row>
      <ToastDialog
        message={toastInfo.message || ""}
        showToast={toastInfo.showToast}
        role={toastInfo.role}
        onCloseFn={() => setToastInfo({ showToast: false })}
      ></ToastDialog>
    </React.Fragment>
  );
});

CamFinDetails.propTypes = {
  history: PropTypes.object,
};

CamFinDetails.displayName = "financialSummary"
export default CamFinDetails;
// export default CamFinDetails;
